import clsx from 'clsx'
import { CircleDashedIcon, LoaderCircle, XIcon } from 'lucide-react'
import React from 'react'

import { SolidAlertTriangleIcon } from 'slab/icons/SolidAlertTriangle'
import { SolidCircleCheckIcon } from 'slab/icons/SolidCircleCheck'
import { formatFileSize } from 'utils'

interface FileProps extends React.HTMLAttributes<HTMLDivElement> {
  actions?: React.ReactNode;
  errorMessage?: React.ReactNode;
  name: string;
  size?: number | string;
  status?: 'pending' | 'uploading' | 'success' | 'error'
}

export function File ({ actions, errorMessage, name, size, status, ...props }: FileProps) {
  function renderStatusIcon () {
    switch (status) {
      case 'pending':
        return (
          <div className='text-gray-400'>
            <CircleDashedIcon aria-hidden size={16} />
          </div>
        )
      case 'uploading':
        return (
          <div className='text-gray-500 animate-spin'>
            <LoaderCircle aria-hidden size={16} />
          </div>
        )
      case 'success':
        return (
          <div className='text-green-400 '>
            <SolidCircleCheckIcon aria-hidden size={16} />
          </div>
        )
      case 'error':
        return (
          <div className='text-red-500'>
            <XIcon aria-hidden size={16} />
          </div>
        )
      default:
        return null
    }
  }

  return (
    <li>
      <div
        {...props}
        aria-label={props['aria-label'] ?? 'File'}
        role={props.role ?? 'group'}
        className={clsx(
          'border rounded-lg p-3 flex items-center justify-between gap-3',
          {
            'border-red-200': status === 'error',
            'border-gray-200': status !== 'error',
          }
        )}
      >
        <div className='flex gap-2 min-w-0'>
          {status && (
            <div className='flex items-center justify-center w-5 h-5'>
              <span className='sr-only'>Status: {status}</span>
              {renderStatusIcon()}
            </div>
          )}
          <div className='flex flex-col gap-0.5 min-w-0'>
            <div className='body text-gray-800 truncate' title={name}>
              <span className='sr-only'>File name: </span>
              {name}
            </div>
            {(!!size || size === 0) && (
              <div className='body-sm text-gray-500'>
                <span className='sr-only'>File size: </span>
                {formatFileSize(size)}
              </div>
            )}
          </div>
        </div>
        {actions && (
          <div className='flex items-center gap-2 shrink-0'>{actions}</div>
        )}
      </div>
      {errorMessage && (
        <div className='body-sm text-red-500 flex items-center gap-1 mt-1'>
          <div className='text-red-400'>
            <SolidAlertTriangleIcon size={16} />
          </div>
          <div className='body-sm-bold text-red-700'>{errorMessage}</div>
        </div>
      )}
    </li>
  )
}

File.displayName = 'FileList.File'
